import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { format } from "date-fns";
import parse from "html-react-parser";

// MUI Components
import {
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Icons
import { ReactComponent as CloseIcon } from "assets/close.svg";
import { ReactComponent as UPIIcon } from "assets/upi.svg";
import { ReactComponent as BankIcon } from "assets/bank.svg";
import { ReactComponent as CouponIcon } from "assets/coupon.svg";
import { ReactComponent as WalletIcon } from "assets/wallet.svg";
import { ReactComponent as CashIcon } from "assets/cash.svg";

// Local Components
import BankForm from "../BankForm";
import { StyledRefundDetails } from "./styled";

// Reducers
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";

// API
import { submitDetailsApi, verifyBankDetailsApi } from "utils/api";
import { getApiErrorMessage } from "utils/apiError";

const BANK_VERIFICATION_FALLBACK = "Something is wrong. Please try again.";
const SUBMIT_DETAILS_FALLBACK = "Error! Please try again in some time.";

const ReturnDetails = ({
  infoType,
  returnType,
  returnItems,
  setReturnItems,
}) => {
  const location = useLocation();
  const failed_statuses = [
    "fail",
    "expire",
    "reversal",
    "cancel",
    "deactivate",
  ];

  const success_statuses = ["processed", "complete", "redeemed"];

  // Selectors
  const rmsConfig = useSelector(getRmsConfig);
  const merchantConfig = useSelector(getMerchantConfig);

  // States
  const [submitDetails, setSubmitDetails] = useState(false);
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [detailsMethod, setDetailsMethod] = useState(null);
  const [submittingDetails, setSubmittingDetails] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  const renderIcon = (sourceValue = "") => {
    const iconList = [
      ["gift_card", <CardGiftcardIcon style={{ fontSize: 20 }} />],
      ["upi", <UPIIcon style={{ fontSize: 14 }} />],
      ["bank_transfer", <BankIcon style={{ fontSize: 12 }} />],
      ["refund_to_source", <BankIcon style={{ fontSize: 12 }} />],
      ["coupon", <CouponIcon style={{ fontSize: 14 }} />],
      ["cash_on_delivery", <CashIcon />],
      ["cashgram_razorpay", <BankIcon style={{ fontSize: 12 }} />],
    ];

    if (!sourceValue) return;

    const lower = sourceValue?.toLowerCase();
    const match = iconList.find(([key]) => lower.includes(key));

    // return the matched icon or the default one
    return match ? match[1] : <WalletIcon style={{ fontSize: 14 }} />;
  };

  const formatRefundMethod = (method) => {
    if (method === "bank_transfer") {
      return "Bank transfer";
    }
    if (method === "upi") {
      return "UPI";
    }
    if (method === "refund_to_source") {
      return "Your refund will be processed to your original mode of payment";
    }
    if (method === "coupon") {
      return rmsConfig.coupon_method_name;
    }
    return method?.replaceAll("_", " ");
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const handleSubmitUPIDetails = async ({ upi }) => {
    setSubmittingDetails(true);
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    const phone =
      returnItems?.customer_phone_number ||
      returnItems?.user_phone_number ||
      null;

    try {
      if (shouldVerify) {
        await verifyBankDetailsApi({
          ifsc: null,
          account_number: null,
          name: null,
          phone: phone ? Number(phone) : null,
          vpa: upi,
        });
      }

      await submitDetailsApi(
        merchantConfig.merchant_id,
        returnItems.id,
        "upi",
        "",
        0,
        0,
        "",
        upi
      );
      setDetailsSubmitted(true);
      setReturnItems({
        ...returnItems,
        refund_json: {
          upi: {
            upi_code: upi,
          },
        },
      });
      toast.success("Details have been successfully submitted", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setSubmitDetails(false);
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        shouldVerify ? BANK_VERIFICATION_FALLBACK : SUBMIT_DETAILS_FALLBACK
      );
      setVerificationError(errorMessage);
      toast.error(
        errorMessage,
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setSubmittingDetails(false);
    }
  };

  const handleSubmitBankDetails = async ({
    accountnum,
    name,
    confirmAccountnum,
    ifsc,
  }) => {
    setSubmittingDetails(true);
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    const phone =
      returnItems?.customer_phone_number ||
      returnItems?.user_phone_number ||
      null;

    try {
      if (shouldVerify) {
        await verifyBankDetailsApi({
          ifsc,
          account_number: Number(accountnum),
          name,
          phone: phone ? Number(phone) : null,
          vpa: null,
        });
      }

      await submitDetailsApi(
        merchantConfig.merchant_id,
        returnItems.id,
        "bank_transfer",
        name,
        Number(accountnum),
        Number(confirmAccountnum),
        ifsc,
        ""
      );
      setDetailsSubmitted(true);
      setReturnItems({
        ...returnItems,
        refund_json: {
          bank_details: {
            bank_account_holder_name: name,
            bank_account_number: accountnum,
          },
        },
      });
      toast.success("Details have been successfully submitted", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setSubmitDetails(false);
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        shouldVerify ? BANK_VERIFICATION_FALLBACK : SUBMIT_DETAILS_FALLBACK
      );
      setVerificationError(errorMessage);
      toast.error(
        errorMessage,
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        }
      );
    } finally {
      setSubmittingDetails(false);
    }
  };

  return (
    <div className="box" id={`${infoType}mode`} style={{ margin: "1rem 0" }}>
      {/* Excahnge Box  */}
      {infoType == "Exchange" ? (
        <div>
          {returnItems &&
            returnItems !== null &&
            returnItems.exchange_details.length > 0 && (
              <div>
                <p className="title"> Exchange Details </p>
                {returnItems.exchange_details.map((detail) => {
                  return (
                    <StyledRefundDetails
                      roundedEdgeButtons={
                        merchantConfig.use_rounded_edge_buttons
                      }
                      refundStatus={detail.exchange_status}
                    >
                      <div className="refund-details-header">
                        <div className="refund-details">
                          <div className="refund-title">
                            <p className="refund-amount">
                              {detail.exchange_order_name ||
                                detail.exchange_order_number}
                            </p>
                            <p className="refund-status">
                              {detail?.exchange_status?.replaceAll("_", " ")}
                            </p>
                          </div>
                        </div>
                        <div className="refund-time">
                          <p>
                            {format(
                              new Date(detail.exchange_initiated_at),
                              "dd MMM yyyy"
                            )}
                          </p>
                          <p>
                            {format(
                              new Date(detail.exchange_initiated_at),
                              "hh:mm aa"
                            )}
                          </p>
                        </div>
                      </div>
                      <a
                        style={{ color: "inherit", textDecoration: "none" }}
                        target="_blank"
                        rel="noreferrer"
                        href={detail.exchange_order_url}
                      >
                        <Button
                          style={{
                            margin: "10px 0px",
                            textTransform: "capitalize",
                          }}
                          variant="outlined"
                          fullWidth
                        >
                          View Order
                          <OpenInNewIcon
                            style={{ marginLeft: "4px", fontSize: "14px" }}
                          />
                        </Button>
                      </a>
                    </StyledRefundDetails>
                  );
                })}
              </div>
            )}
        </div>
      ) : (
        (returnType == "refund" || returnType == "refund_exchange") &&
        infoType == "Return" && (
          <div>
            {returnItems?.refund_details &&
              returnItems.refund_details.length > 0 && (
                <div>
                  <p className="title">Refund Details</p>
                  {returnItems.refund_details.map((detail) => {
                    return (
                      <div>
                        <StyledRefundDetails
                          roundedEdgeButtons={
                            merchantConfig.use_rounded_edge_buttons
                          }
                          refundStatus={detail.refund_status}
                        >
                          <div className="refund-details-header">
                            <div className="refund-details">
                              <div className="refund-title">
                                <p className="refund-amount">
                                  ₹ {detail.refund_amount}
                                </p>
                                <p className="refund-status">
                                  {detail?.refund_status?.replaceAll("_", " ")}
                                </p>
                              </div>
                              <p className="refund-mode">
                                {renderIcon(detail?.refund_method || "")}
                                <span>
                                  {detail?.refund_method?.replaceAll("_", " ")}
                                </span>
                              </p>
                            </div>
                            <div className="refund-time">
                              <p>
                                {format(
                                  new Date(detail.refund_initiated_at),
                                  "dd MMM yyyy"
                                )}
                              </p>
                              <p>
                                {format(
                                  new Date(detail.refund_initiated_at),
                                  "hh:mm aa"
                                )}
                              </p>
                            </div>
                          </div>
                          <div>
                            {(detail.refund_method === "bank_transfer" ||
                              detail.refund_method ===
                                "bank_transfer_razorpayx" ||
                              detail.refund_method ===
                                "bank_transfer_cashfree") &&
                            rmsConfig.use_direct_transfer_for_refund ? (
                              <div className="account-details">
                                <hr />
                                {detail.account_holder_name &&
                                  detail.account_holder_name !== null && (
                                    <p>
                                      {detail.account_holder_name} &nbsp; -
                                      &nbsp;{" "}
                                      {detail.account_number.replace(
                                        /^(\d+)(\d{4})/,
                                        "*******$2"
                                      )}
                                    </p>
                                  )}
                                {detail.vpa !== null && (
                                  <p>
                                    UPI ID:{" "}
                                    {detail.vpa.replace(
                                      /^(\d+)(\d{4})/,
                                      "*******$2"
                                    )}
                                  </p>
                                )}
                              </div>
                            ) : (
                              location.pathname === "/tracking" &&
                              (detail.refund_method == "bank_transfer" ||
                                detail.refund_method == "upi") &&
                              !detailsSubmitted && (
                                <div>
                                  <hr />
                                  <p
                                    style={{
                                      color: "rgb(51, 51, 51)",
                                      fontSize: "16px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    Please select the mode of refund
                                  </p>
                                  <FormControl>
                                    <RadioGroup
                                      row
                                      aria-labelledby="refund-options"
                                      name="refund-options"
                                    >
                                      {rmsConfig.customer_bank_enabled && (
                                        <FormControlLabel
                                          id="bank"
                                          value="transfer"
                                          control={
                                            <Radio
                                              color="default"
                                              size="small"
                                              onChange={() => {
                                                setSubmitDetails(true);
                                                setDetailsMethod("Bank");
                                                setVerificationError("");
                                              }}
                                            />
                                          }
                                          label={
                                            <p className="radio-label">
                                              Bank Account
                                            </p>
                                          }
                                        />
                                      )}
                                      {rmsConfig.customer_upi_enabled && (
                                        <FormControlLabel
                                          id="upi"
                                          value="upi"
                                          control={
                                            <Radio
                                              color="default"
                                              size="small"
                                              onChange={() => {
                                                setSubmitDetails(true);
                                                setDetailsMethod("UPI");
                                                setVerificationError("");
                                              }}
                                            />
                                          }
                                          label={
                                            <p className="radio-label">UPI</p>
                                          }
                                        />
                                      )}
                                    </RadioGroup>
                                  </FormControl>

                                  <Drawer
                                    classes={{ paper: "paper-drawer" }}
                                    anchor="bottom"
                                    open={submitDetails}
                                    onClose={() => {
                                      setSubmitDetails(false);
                                      setVerificationError("");
                                    }}
                                  >
                                    <div>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          boxShadow:
                                            "0px 2px 4px rgba(0, 0, 0, 0.08)",
                                          height: "80px",
                                          padding: "32px",
                                        }}
                                      >
                                        <p
                                          style={{
                                            fontSize: "1rem",
                                            fontWeight: "500",
                                            color: "#3B444D",
                                          }}
                                        >
                                          Enter {detailsMethod} Details
                                        </p>
                                        <p
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            setSubmitDetails(false);
                                            setVerificationError("");
                                          }}
                                        >
                                          {" "}
                                          <CloseIcon
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              stroke: "#3B444D",
                                            }}
                                          />{" "}
                                        </p>
                                      </div>
                                      {detailsMethod == "Bank" ? (
                                        <BankForm
                                          handleSubmitBankDetails={
                                            handleSubmitBankDetails
                                          }
                                          submittingDetails={submittingDetails}
                                          errorMessage={verificationError}
                                        />
                                      ) : (
                                        <form
                                          onSubmit={handleSubmit(
                                            handleSubmitUPIDetails
                                          )}
                                          style={{ padding: "20px" }}
                                        >
                                          <Controller
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                              required: true,
                                              validate: (value) =>
                                                !/@paytm$/i.test(value) ||
                                                "Paytm UPI IDs are not supported",
                                              pattern: {
                                                value:
                                                  /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
                                                message: "Invalid UPI ID",
                                              },
                                            }}
                                            name="upi"
                                            render={({ field }) => (
                                              <TextField
                                                variant="outlined"
                                                fullWidth
                                                id="upi"
                                                label="UPI ID"
                                                error={Boolean(errors.upi)}
                                                helperText={
                                                  errors.upi
                                                    ? errors.upi.type ===
                                                      "validate"
                                                      ? `Paytm UPI IDs are not supported`
                                                      : errors.upi.type ===
                                                        "pattern"
                                                      ? "Invalid UPI ID"
                                                      : "UPI code is required"
                                                    : ""
                                                }
                                                inputProps={{ type: "text" }}
                                                style={{ marginTop: "20px" }}
                                                {...field}
                                              />
                                            )}
                                          />
                                          {verificationError && (
                                            <p
                                              style={{
                                                color: "#D32F2F",
                                                marginTop: "12px",
                                              }}
                                            >
                                              {verificationError}
                                            </p>
                                          )}
                                          <Button
                                            type="submit"
                                            color="primary"
                                            variant="contained"
                                            disabled={submittingDetails}
                                            style={{
                                              padding: "1rem",
                                              borderRadius:
                                                !merchantConfig.use_rounded_edge_buttons
                                                  ? "0px"
                                                  : "4px",
                                              margin: "40px auto",
                                              display: "block",
                                              cursor: "pointer",
                                              textTransform: "capitalize",
                                            }}
                                          >
                                            {submittingDetails
                                              ? "Submitting..."
                                              : "Submit"}
                                          </Button>
                                        </form>
                                      )}
                                    </div>
                                  </Drawer>
                                </div>
                              )
                            )}

                            {detail.refund_method === "upi" &&
                              detail.vpa !== "" && (
                                <p className="account-details">
                                  <hr />
                                  UPI ID:{" "}
                                  {detail.vpa.replace(
                                    /^(\d+)(\d{4})/,
                                    "*******$2"
                                  )}
                                </p>
                              )}
                            {detail.refund_method === "coupon" && (
                              <div className="account-details">
                                <hr />
                                <p>
                                  {detail.refund_id !== null && (
                                    <span>
                                      Coupon Code:{" "}
                                      <span style={{ fontWeight: "600" }}>
                                        {detail.refund_id}
                                      </span>
                                    </span>
                                  )}
                                </p>{" "}
                                {detail.refund_amount !== null && (
                                  <p>
                                    Coupon discount:{" "}
                                    <span style={{ fontWeight: "600" }}>
                                      ₹ {detail.refund_amount}
                                    </span>
                                  </p>
                                )}
                              </div>
                            )}
                            {detail.refund_method === "gift_card" && (
                              <div className="account-details">
                                <hr />
                                <p>
                                  Gift Card Code:{" "}
                                  <span style={{ fontWeight: "600" }}>
                                    {detail.giftcard_code}
                                  </span>
                                </p>
                              </div>
                            )}
                          </div>
                          {detail?.refund_url !== null &&
                            !(
                              success_statuses.some((s) =>
                                detail?.refund_status
                                  .toLowerCase()
                                  .includes(s.toLowerCase())
                              ) ||
                              failed_statuses.some((s) =>
                                detail?.refund_status
                                  .toLowerCase()
                                  .includes(s.toLowerCase())
                              )
                            ) && (
                              <div>
                                <hr />
                                <a
                                  href={detail.refund_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: "inherit" }}
                                >
                                  <Button
                                    color="primary"
                                    variant="contained"
                                    fullWidth
                                  >
                                    Claim your refund
                                  </Button>
                                </a>
                              </div>
                            )}
                        </StyledRefundDetails>
                      </div>
                    );
                  })}
                </div>
              )}

            {(!returnItems?.refund_details ||
              returnItems.refund_details.length === 0) &&
              returnItems?.customer_preferred_refund_method && (
                <div>
                  {location.pathname !== "/return-cart" && (
                    <p
                      className="refund-preference-title"
                      style={{
                        fontWeight: "600",
                        margin: "1rem 0px",
                      }}
                    >
                      Refund Details
                    </p>
                  )}
                  <p
                    className="refund-preference"
                    style={{
                      margin: "0px 0px 10px",
                      color: "#3B444D",
                      textTransform: "capitalize",
                    }}
                  >
                    Refund preference selected&nbsp; - &nbsp;
                    <span style={{ fontWeight: "600" }}>
                      {formatRefundMethod(
                        returnItems.customer_preferred_refund_method
                      )}
                    </span>
                  </p>
                </div>
              )}

            {rmsConfig.brand_refund_note &&
              rmsConfig.brand_refund_note !== null &&
              rmsConfig.brand_refund_note !== "" && (
                <div
                  style={{
                    marginTop: "10px",
                    color: "rgba(51, 51, 51, 0.7)",
                    listStyle: "inside",
                  }}
                  className="refund-note"
                >
                  <p
                    style={{
                      color: "#DD435D99",
                      marginRight: "5px",
                      fontWeight: "600",
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                      marginBottom: "10px",
                      fontSize: "16px",
                    }}
                  >
                    Refund Note:
                  </p>
                  {parse(rmsConfig.brand_refund_note)}
                </div>
              )}

            {rmsConfig.brand_exchange_note &&
              rmsConfig.brand_exchange_note !== null &&
              rmsConfig.brand_exchange_note !== "" && (
                <div
                  style={{
                    marginTop: "10px",
                    color: "rgba(51, 51, 51, 0.7)",
                    listStyle: "inside",
                  }}
                  className="refund-note"
                >
                  <p
                    style={{
                      color: "#DD435D99",
                      marginRight: "5px",
                      fontWeight: "600",
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                      marginBottom: "10px",
                      fontSize: "16px",
                    }}
                  >
                    {rmsConfig.only_replacement ? "Replacement" : "Exchange"}{" "}
                    Note:
                  </p>
                  {parse(rmsConfig.brand_exchange_note)}
                </div>
              )}
          </div>
        )
      )}
    </div>
  );
};

export default ReturnDetails;
